import React from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CalendarCheck, ArrowLeft, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PageHero } from "@/components/site/PageHero";
import { CTASection } from "@/components/site/CTASection";
import { Reveal } from "@/components/motion/Reveal";
import { FALLBACK_IMAGES } from "@/lib/assets";
import NotFound from "./NotFound";

const Blocks = ({ blocks }) => (
  <div className="space-y-4">
    {blocks.map((b, bi) => {
      if (b.type === "p")
        return (
          <p key={bi} className="text-base leading-relaxed text-deep-ink/80">
            {b.text}
          </p>
        );
      if (b.type === "list")
        return (
          <ul key={bi} className="space-y-2.5 ps-1">
            {b.items.map((li, lii) => (
              <li key={lii} className="flex gap-3 text-base leading-relaxed text-deep-ink/80">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-champagne-gold" />
                <span>{li}</span>
              </li>
            ))}
          </ul>
        );
      if (b.type === "qa")
        return (
          <div key={bi} className="rounded-2xl bg-sand/70 p-5">
            <p className="font-serif text-lg text-deep-ink">{b.q}</p>
            <p className="mt-2 text-base leading-relaxed text-deep-ink/75">{b.a}</p>
          </div>
        );
      return null;
    })}
  </div>
);

/** Generic detail page for life stages, expertise, approach, service, legal. */
export const DetailPage = ({ ns, itemsKey, base, baseLabelKey, imageMap, kind = "content" }) => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const items = t(itemsKey, { returnObjects: true }) || [];
  const item = items.find((i) => i.slug === slug);

  if (!item) return <NotFound />;

  const isLegal = kind === "legal";
  const mapImage = imageMap ? imageMap[slug] : null;
  // Auf Wunsch der Praxis ausgeblendet (alle Sprachen): FAQ + „Geeigneter Termin"
  const HIDDEN_SECTION_HEADINGS = new Set([
    "Häufig gestellte Fragen", "Frequently asked questions", "Sık sorulan sorular", "الأسئلة الشائعة",
    "Geeigneter Termin", "Suitable appointment", "Uygun randevu", "الموعد المناسب",
  ]);
  const visibleSections = (Array.isArray(item.sections) ? item.sections : []).filter(
    (s) => !HIDDEN_SECTION_HEADINGS.has(((s && s.heading) || "").trim())
  );
  const hasSections = visibleSections.length > 0;
  const image = isLegal ? null : mapImage || (hasSections ? FALLBACK_IMAGES.clinic : null);
  const related = items.filter((i) => i.slug !== slug).slice(0, 3);

  return (
    <div data-testid={`detail-${slug}`}>
      <PageHero
        kicker={t(`${ns}.hubKicker`, "")}
        title={item.title}
        text={item.summary || item.excerpt}
        image={image}
      />

      <section className="bg-warm-ivory">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-10 lg:py-24">
          {hasSections ? (
            <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
              {/* Main column: progressive-disclosure accordions */}
              <div className="lg:col-span-8">
                <Reveal>
                  <p className="mb-8 font-serif text-2xl leading-snug text-deep-ink/90 lg:text-3xl">
                    <span className="me-3 inline-block h-6 w-px translate-y-1 bg-champagne-gold align-middle" />
                    {item.summary || item.excerpt}
                  </p>
                </Reveal>
                <Reveal delay={0.05}>
                  <Accordion type="multiple" defaultValue={["s-0", "s-1"]} className="w-full">
                    {visibleSections.map((sec, si) => (
                      <AccordionItem
                        key={si}
                        value={`s-${si}`}
                        data-testid={`section-${si}`}
                        className="border-b border-deep-ink/10"
                      >
                        <AccordionTrigger className="py-6 text-start hover:no-underline">
                          <span className="flex items-center gap-4">
                            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-champagne-gold/12 font-serif text-sm text-champagne-gold">
                              {si + 1}
                            </span>
                            <span className="font-serif text-xl text-deep-ink lg:text-2xl">{sec.heading}</span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="pb-8 ps-12">
                          <Blocks blocks={sec.blocks} />
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </Reveal>

                <Reveal>
                  <Link to={base} className="mt-10 inline-flex items-center gap-2 text-sm font-medium text-champagne-gold">
                    <ArrowLeft className="rtl-flip h-4 w-4" />
                    {t("common.backTo")} {t(baseLabelKey)}
                  </Link>
                </Reveal>
              </div>

              {/* Sticky trust + booking rail */}
              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-28">
                  <Reveal delay={0.1}>
                    <div className="rounded-3xl bg-sand p-8" data-testid="detail-trust-card">
                      <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-editorial text-muted-sage">
                        <Sparkles strokeWidth={1.5} className="h-4 w-4" />
                        {t("detail.physicianLed", "Physician-led care")}
                      </span>
                      <p className="mt-4 font-serif text-2xl text-deep-ink">
                        {t("doctor.name", "Dr. med. Hülya Acar-Nosir")}
                      </p>
                      <p className="mt-1 text-sm text-deep-ink/70">
                        {t("detail.physicianRole", "Founder & Physician · Women's Health, New Cairo")}
                      </p>
                      <ul className="mt-6 space-y-3">
                        {[
                          t("detail.trust1", "One physician, one connected perspective"),
                          t("detail.trust2", "Evidence-based, individually explained"),
                          t("detail.trust3", "Unhurried, confidential consultations"),
                        ].map((line, k) => (
                          <li key={k} className="flex gap-3 text-sm leading-relaxed text-deep-ink/80">
                            <ShieldCheck strokeWidth={1.5} className="mt-0.5 h-4 w-4 flex-shrink-0 text-champagne-gold" />
                            <span>{line}</span>
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/book"
                        data-testid="detail-book-cta"
                        className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-deep-ink px-6 py-4 text-sm font-medium text-warm-ivory transition-colors duration-300 hover:bg-champagne-gold hover:text-deep-ink"
                      >
                        <CalendarCheck className="h-4 w-4" />
                        {t("nav.book", "Book a consultation")}
                      </Link>
                    </div>
                  </Reveal>
                </div>
              </aside>
            </div>
          ) : (
            <div className="max-w-3xl">
              {item.body && item.body.length > 0 && (
                <Reveal>
                  <div className="space-y-6">
                    {item.body.map((p, i) => (
                      <p key={i} className="text-lg leading-relaxed text-deep-ink/80">
                        {p}
                      </p>
                    ))}
                  </div>
                </Reveal>
              )}



              <Reveal>
                <Link to={base} className="mt-12 inline-flex items-center gap-2 text-sm font-medium text-champagne-gold">
                  <ArrowLeft className="rtl-flip h-4 w-4" />
                  {t("common.backTo")} {t(baseLabelKey)}
                </Link>
              </Reveal>
            </div>
          )}
        </div>
      </section>

      {!isLegal && related.length > 0 && (
        <section className="bg-sand">
          <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10">
            <h2 className="mb-10 font-serif text-3xl text-deep-ink">{t("common.explore")}</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r, i) => (
                <Reveal key={r.slug} delay={i * 0.06}>
                  <Link
                    to={`${base}/${r.slug}`}
                    data-testid={`related-${r.slug}`}
                    className="group flex h-full flex-col rounded-3xl border border-transparent bg-warm-ivory p-7 transition-colors duration-500 hover:border-champagne-gold/45"
                  >
                    <h3 className="font-serif text-xl text-deep-ink group-hover:text-champagne-gold">{r.title}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-deep-ink/70 line-clamp-3">
                      {r.summary || r.excerpt}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-2 text-sm text-champagne-gold">
                      {t("common.readMore")}
                      <ArrowRight className="rtl-flip h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </div>
  );
};
